import get from 'lodash-es/get';
import pick from 'lodash-es/pick';
import type { DeepReadonly } from 'ts-essentials';

import type { AnalyticsDeliveryAddress } from '@jane/shared-ecomm/types';
/* eslint-disable-next-line no-restricted-imports */
import type { Cart, PendingCartProduct } from '@jane/shared/models';
import type {
  CartLineItems,
  Cart as zCart,
  PendingCartProduct as zPendingCartProduct,
} from '@jane/shared/types';

import { postAnalyticsEvent } from './postAnalyticsEvent';

export const cartProductList = (
  cartProducts:
    readonly DeepReadonly<PendingCartProduct>[] | zPendingCartProduct[]
) =>
  cartProducts.map((product) => {
    const analyticsProperties = pick(product, [
      'product_id',
      'name',
      'brand',
      'category',
      'kind',
      'count',
      'special_id',
      'special_title',
      'price_id',
    ]);
    const unit_price = Number(
      get(product, `discounted_price_${product.price_id}`) ||
        get(product, `price_${product.price_id}`)
    );

    return {
      ...analyticsProperties,
      unit_price,
    };
  });

const cartBrandNamesAndIds = (
  cartProducts:
    readonly DeepReadonly<PendingCartProduct>[] | zPendingCartProduct[]
) => {
  const brandNames = cartProducts
    .filter((p) => !!p.brand)
    .map((p) => p.brand?.trim())
    .sort();

  const brandIds = cartProducts
    .filter((p) => !!p.product_brand_id)
    .map((p) => p.product_brand_id)
    .sort();

  return {
    names: Array.from(new Set(brandNames)).join(','),
    ids: Array.from(new Set(brandIds)).join(','),
  };
};

interface PostCheckoutEventArgs {
  cart: DeepReadonly<Cart> | zCart;
  cartLineItems: Partial<CartLineItems> & { total: CartLineItems['total'] };
  deliveryAddress?: AnalyticsDeliveryAddress | null;
  isStackAdaptPixelEnabled: boolean;
  orderCustomer: {
    birthDate?: string | null;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
  };
}

export const postCheckoutAnalyticsEvent = ({
  cart,
  cartLineItems,
  deliveryAddress,
  isStackAdaptPixelEnabled,
  orderCustomer,
}: PostCheckoutEventArgs) => {
  const cartProducts = cartProductList(cart.products);
  const cartBrands = cartBrandNamesAndIds(cart.products);

  const properties = {
    /** @todo Send store currency for analytics events */
    currency: null,
    estimatedTotal: cartLineItems.total,
    deliveryFee: cartLineItems.deliveryFee,
    discountTotal: cartLineItems.discountTotal,
    preDiscountSubtotal: cartLineItems.preDiscountSubtotal,
    salesTax: cartLineItems.salesTax,
    serviceFee: cartLineItems.serviceFee,
    storeId: cart.store?.id,
    storeTax: cartLineItems.storeTax,
    cartId: cart.id!,
    paymentMethod: cart.payment_method,
    products: cartProducts,
    brandNames: cartBrands.names,
    brandIds: cartBrands.ids,
    reservationMode: cart.reservation_mode,
    customerFirstName: orderCustomer.firstName,
    customerLastName: orderCustomer.lastName,
    customerEmail: orderCustomer.email,
    customerPhone: orderCustomer.phone,
    customerBirthDate: orderCustomer.birthDate,
    deliveryWindowStartTime: cart.delivery_started_at,
    deliveryWindowEndTime: cart.delivery_finished_at,
    storeNotes: cart.message,
    deliveryAddress,
    uuid: cart.uuid,
    tags: cart.tags,
    isStackAdaptPixelEnabled,
  };
  postAnalyticsEvent({
    name: 'checkout',
    properties,
  });
};
